import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { get, post, put as apiPut, setToken, setStoredUser, getStoredUser } from '../utils/api';
import { useTheme } from './ThemeContext';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [loginResponse, setLoginResponse] = useState(null);
  const { initFromUser } = useTheme();

  const loadUser = useCallback(async () => {
    setIsLoading(true);

    const cachedUser = getStoredUser();
    if (cachedUser) {
      setUser(cachedUser);
      setIsAuthenticated(true);
      initFromUser(cachedUser.theme_preference);
    }

    try {
      const data = await get('/api/auth/me');
      setUser(data.user);
      setStoredUser(data.user);
      setIsAuthenticated(true);
      initFromUser(data.user?.theme_preference);
    } catch {
      if (!cachedUser) {
        setToken(null);
        setStoredUser(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [initFromUser]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);
    setLoginResponse(null);

    try {
      const data = await post('/api/auth/login', { email, password });

      // Check for non-approved account states
      if (data.status && data.status !== 'success' && !data.access_token) {
        setLoginResponse(data);
        setIsLoading(false);
        return { success: false, status: data.status, data };
      }

      if (data.access_token && data.user) {
        setToken(data.access_token);
        setUser(data.user);
        setStoredUser(data.user);
        setIsAuthenticated(true);
        initFromUser(data.user.theme_preference);
        setIsLoading(false);
        return { success: true };
      }

      setError('Respuesta inesperada del servidor');
      setIsLoading(false);
      return { success: false };
    } catch (err) {
      setError(err.message || 'Error al iniciar sesion');
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  };

  const register = async (userData) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await post('/api/auth/register', userData);

      if (data.account_status === 'pending') {
        setLoginResponse({
          status: 'pending',
          message: data.message,
          account_status: 'pending'
        });
        setIsLoading(false);
        return { success: true, message: data.message, pending: true };
      }

      if (data.access_token) {
        setToken(data.access_token);
        setUser(data.user);
        setStoredUser(data.user);
        setIsAuthenticated(true);
      }

      setIsLoading(false);
      return { success: true, message: data.message };
    } catch (err) {
      setError(err.message || 'Error al registrarse');
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await post('/api/auth/logout', {});
    } catch {
      // Ignore errors on logout
    }
    setToken(null);
    setStoredUser(null);
    setUser(null);
    setIsAuthenticated(false);
    setLoginResponse(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await get('/api/users/me');
      setUser(data.user);
      setStoredUser(data.user);
    } catch {
      // Session expired
    }
  }, []);

  const updateUser = useCallback(async (userData) => {
    try {
      const data = await apiPut('/api/users/me', userData);
      if (data.user) {
        setUser(data.user);
        setStoredUser(data.user);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const clearError = () => setError(null);
  const clearLoginResponse = () => setLoginResponse(null);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    error,
    loginResponse,
    login,
    register,
    logout,
    refreshUser,
    updateUser,
    clearError,
    clearLoginResponse,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
